import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BackupRecoveryMonitor = ({ backupStatus, loading, onRefresh }) => {
  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'active': case'passed':
        return 'text-green-600 bg-green-50';
      case 'in_progress':
        return 'text-blue-600 bg-blue-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const storagePercentage = (backupStatus?.storageUsed / backupStatus?.storageLimit) * 100;

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
          <Icon name="Database" className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
          <span className="break-words">Backup & Disaster Recovery Monitoring</span>
        </h2>
        <Button onClick={onRefresh} variant="outline" size="sm" className="whitespace-nowrap">
          <Icon name="RefreshCw" className="w-4 h-4" />
          <span className="hidden sm:inline ml-2">Refresh</span>
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Backup Status */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4 flex items-center gap-2 flex-wrap">
            <Icon name="HardDrive" className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <span className="break-words">Backup Status</span>
          </h3>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Last Backup</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 break-words">
                {new Date(backupStatus?.lastBackup)?.toLocaleString()}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Status</span>
              <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(backupStatus?.backupStatus)} whitespace-nowrap self-start sm:self-auto`}>
                {backupStatus?.backupStatus?.toUpperCase()}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Data Integrity</span>
              <span className="text-xs sm:text-sm font-medium text-green-600">{backupStatus?.dataIntegrity}%</span>
            </div>
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 mb-2">
                <span className="text-xs sm:text-sm text-gray-600">Storage Used</span>
                <span className="text-xs sm:text-sm font-medium text-gray-900 break-words">
                  {backupStatus?.storageUsed} GB / {backupStatus?.storageLimit} GB
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    storagePercentage > 80 ? 'bg-red-600' : storagePercentage > 60 ? 'bg-yellow-600' : 'bg-green-600'
                  }`}
                  style={{ width: `${storagePercentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Replication Status */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4 flex items-center gap-2 flex-wrap">
            <Icon name="Copy" className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <span className="break-words">Data Replication</span>
          </h3>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Replication Status</span>
              <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(backupStatus?.replicationStatus)} whitespace-nowrap self-start sm:self-auto`}>
                {backupStatus?.replicationStatus?.toUpperCase()}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Primary Region</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900">Asia-Pacific (Sydney)</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Secondary Region</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900">Asia-Pacific (Singapore)</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Replication Lag</span>
              <span className="text-xs sm:text-sm font-medium text-green-600">&lt; 5 seconds</span>
            </div>
          </div>
        </div>

        {/* Recovery Testing */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4 flex items-center gap-2 flex-wrap">
            <Icon name="RotateCcw" className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <span className="break-words">Recovery Testing</span>
          </h3>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Last Test Date</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 break-words">
                {new Date(backupStatus?.recoveryTestDate)?.toLocaleDateString()}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Test Result</span>
              <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(backupStatus?.recoveryTestResult)} whitespace-nowrap self-start sm:self-auto`}>
                {backupStatus?.recoveryTestResult?.toUpperCase()}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Recovery Time Objective (RTO)</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900">4 hours</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Recovery Point Objective (RPO)</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900">1 hour</span>
            </div>
          </div>
        </div>

        {/* Business Continuity Metrics */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4 flex items-center gap-2 flex-wrap">
            <Icon name="Activity" className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <span className="break-words">Business Continuity</span>
          </h3>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Uptime (30 days)</span>
              <span className="text-xs sm:text-sm font-medium text-green-600">99.98%</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Backup Success Rate</span>
              <span className="text-xs sm:text-sm font-medium text-green-600">100%</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Disaster Recovery Plan</span>
              <span className="text-xs sm:text-sm font-medium text-green-600">Active</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Next Scheduled Test</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 break-words">
                {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="mt-6 flex items-center gap-3">
        <Button variant="primary" className="flex items-center gap-2">
          <Icon name="Play" className="w-4 h-4" />
          Run Backup Now
        </Button>
        <Button variant="outline" className="flex items-center gap-2">
          <Icon name="TestTube" className="w-4 h-4" />
          Test Recovery
        </Button>
        <Button variant="outline" className="flex items-center gap-2">
          <Icon name="FileText" className="w-4 h-4" />
          View DR Plan
        </Button>
      </div>
    </div>
  );
};

export default BackupRecoveryMonitor;